// (c) Scan answer path: webcam capture OR file upload, preview, S3 upload bar.
import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import Webcam from 'react-webcam';
import axios from 'axios';
import { UPLOAD_CONTENT_TYPES } from '@cwy/config/contracts';
import { api, uploadToS3 } from '@/lib/api';

interface ScanUploadProps {
  questionId: string;
  studentPaperId: string;
  disabled?: boolean;
  /** Called with the stored S3 key once upload finishes. */
  onUploaded: (s3Key: string) => void;
}

interface PresignResponse {
  uploadUrl: string;
  s3Key: string;
}

// Narrowed from `image/*`, which let the picker offer files the presign
// allowlist rejects — a GIF or a BMP came back as a 400 the student could not
// interpret. Derived from the contract so the two cannot drift.
const ACCEPTED_TYPES = Object.keys(UPLOAD_CONTENT_TYPES).join(',');

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

/**
 * Turn an upload failure into something the student can act on.
 *
 * The API's own message is already student-safe — every response uses the
 * `{ success, error }` envelope and never leaks internals — so prefer it over a
 * guess. Only a genuinely response-less failure is a connection problem, and
 * conflating the two is what made this unreportable: a 409 on an
 * already-submitted paper and a blocked CORS preflight read identically.
 */
function uploadErrorMessage(err: unknown, stage: 'presign' | 'put'): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const serverMessage = (err.response?.data as { error?: string } | undefined)?.error;

    if (stage === 'presign') {
      if (serverMessage) return serverMessage;
      if (status === 429) return 'Too many uploads just now. Wait a moment and try again.';
      if (status) return `Could not start the upload (${status}). Please try again.`;
    } else if (status) {
      // The PUT reached S3 and was refused — a bucket or credential problem, not
      // anything the student can fix by retrying.
      return `The file was rejected by storage (${status}). Please report this.`;
    }

    if (err.code === 'ECONNABORTED') {
      return 'The upload timed out. Try again, or use a smaller photo.';
    }
  }
  return 'Upload failed. Please check your connection and try again.';
}

export function ScanUpload({ questionId, studentPaperId, disabled, onUploaded }: ScanUploadProps) {
  const webcamRef = useRef<Webcam>(null);
  const [mode, setMode] = useState<'camera' | 'file'>('camera');
  const [preview, setPreview] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback(async () => {
    const shot = webcamRef.current?.getScreenshot();
    if (!shot) return;
    setPreview(shot);
    setBlob(await dataUrlToBlob(shot));
  }, []);

  const onFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBlob(file);
    setPreview(URL.createObjectURL(file));
  }, []);

  const retake = () => {
    setPreview(null);
    setBlob(null);
    setProgress(null);
    setError(null);
  };

  const upload = useCallback(async () => {
    if (!blob) return;
    setError(null);
    setProgress(0);
    // Which leg failed. A bare `catch` reported every cause as a connection
    // problem, including the ones the server had already explained.
    let stage: 'presign' | 'put' = 'presign';
    const contentType = blob.type || 'image/jpeg';
    try {
      // 1) Ask the Core API for a presigned PUT URL + the S3 key.
      const { data } = await api.post<{ data: PresignResponse }>('/uploads/presign', {
        studentPaperId,
        questionId,
        contentType,
      });
      const { uploadUrl, s3Key } = data.data;

      // 2) PUT the image straight to S3.
      //
      // This MUST go through `uploadToS3`, not the shared `api` instance. `api`
      // sets `withCredentials: true` for the session cookie, which turns the PUT
      // into a credentialed CORS request — and S3 never returns
      // `Access-Control-Allow-Credentials`, so the browser blocks the response no
      // matter how the bucket's CORS rule is written. It surfaced as an opaque
      // network error, i.e. "check your connection". Clearing `baseURL` alone was
      // not enough: interceptors and defaults live on the instance.
      stage = 'put';
      await uploadToS3(uploadUrl, blob, contentType, setProgress);

      setProgress(100);
      onUploaded(s3Key);
    } catch (err) {
      setError(uploadErrorMessage(err, stage));
      setProgress(null);
    }
  }, [blob, questionId, studentPaperId, onUploaded]);

  return (
    <div>
      {!preview && (
        <>
          <div className="mb-2 flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => setMode('camera')}
              className={`rounded-full px-3 py-1 ${mode === 'camera' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              Camera
            </button>
            <button
              type="button"
              onClick={() => setMode('file')}
              className={`rounded-full px-3 py-1 ${mode === 'file' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              Upload file
            </button>
          </div>

          {mode === 'camera' ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-black">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: 'environment' }}
                className="w-full"
              />
            </div>
          ) : (
            <label className="flex h-40 w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-sm text-slate-500">
              Tap to choose a photo of your answer
              <input
                type="file"
                accept={ACCEPTED_TYPES}
                className="hidden"
                onChange={onFile}
                disabled={disabled}
              />
            </label>
          )}

          {mode === 'camera' && (
            <button
              type="button"
              onClick={capture}
              disabled={disabled}
              className="mt-3 w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:opacity-40"
            >
              Capture
            </button>
          )}
        </>
      )}

      {preview && (
        <div>
          <img src={preview} alt="Answer preview" className="w-full rounded-xl border border-slate-200" />

          {progress !== null && (
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${progress}%`, backgroundColor: 'var(--bin-blue)' }}
              />
            </div>
          )}
          {error && <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>{error}</p>}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={retake}
              disabled={progress !== null && progress < 100}
              className="flex-1 rounded-xl border border-slate-300 px-4 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Retake
            </button>
            <button
              type="button"
              onClick={upload}
              disabled={disabled || (progress !== null && progress < 100)}
              className="flex-1 rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:opacity-40"
            >
              {progress === null ? 'Submit scan' : progress < 100 ? 'Uploading…' : 'Uploaded'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
